import { useRef, useState } from 'react';
import { calculatePositionInRange } from './draggable-helpers.ts';
import { DraggablePosition, VIEWPORT_GAP } from './useDraggablePositions.ts';

type UseDraggableEventsProps = {
  disabled: boolean;
  draggableDivRef: React.RefObject<HTMLDivElement | null>;
  setPosition: React.Dispatch<React.SetStateAction<DraggablePosition | null>>;
};

export const useDraggableEvents = ({
  disabled,
  draggableDivRef,
  setPosition,
}: UseDraggableEventsProps) => {
  const dragPointerIdRef = useRef<number | null>(null);
  const dragShiftRef = useRef({ x: 0, y: 0 });

  const [isDragging, setIsDragging] = useState(false);

  // Returns false if the target is an interactive HTML element that should not start dragging.t
  const canStartDragging = (target: HTMLElement | null) => {
    if (!target) {
      return false;
    }

    return !target.closest(
      'button, a, input, textarea, select, option, label, summary, [role="button"], [role="link"], [data-draggable-cancel="true"]',
    );
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const leftMouseButton = event.button == 0;
    if (
      disabled ||
      !leftMouseButton ||
      !draggableDivRef.current ||
      !canStartDragging(event.target as HTMLElement | null)
    )
      return;

    const draggableDivRect = draggableDivRef.current.getBoundingClientRect();

    dragPointerIdRef.current = event.pointerId;
    dragShiftRef.current = {
      x: event.clientX - draggableDivRect.left,
      y: event.clientY - draggableDivRect.top,
    };

    setIsDragging(true);
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (
      disabled ||
      dragPointerIdRef.current !== event.pointerId ||
      !draggableDivRef.current
    )
      return;

    const draggableDivRect = draggableDivRef.current.getBoundingClientRect();

    const maxLeft = window.innerWidth - draggableDivRect.width - VIEWPORT_GAP;
    const maxTop = window.innerHeight - draggableDivRect.height - VIEWPORT_GAP;

    setPosition({
      left: calculatePositionInRange(
        event.clientX - dragShiftRef.current.x,
        VIEWPORT_GAP,
        maxLeft,
      ),
      top: calculatePositionInRange(
        event.clientY - dragShiftRef.current.y,
        VIEWPORT_GAP,
        maxTop,
      ),
    });
  };

  const stopDragging = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragPointerIdRef.current !== event.pointerId) return;

    dragPointerIdRef.current = null;
    setIsDragging(false);

    if (typeof event.currentTarget.releasePointerCapture === 'function') {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return {
    isDragging,
    onPointerDown,
    onPointerMove,
    onPointerUp: stopDragging,
    onPointerCancel: stopDragging,
  };
};
